import { transpose } from '../utils/transpose';
import { replaceChords } from '../utils/chordPatterns';

const TEXT_AREA_ID = 'currentLyrics';

const replaceTextAt = (textContent, startAt, replacement, length) => {
  if (startAt < 0 || startAt >= textContent.length || length < 0) {
    return textContent;
  }

  const before = textContent.substring(0, startAt);
  const after = textContent.substring(startAt + length);

  return before + replacement + after;
};

// Falls back to the full text when nothing is selected, so toolbar actions can apply to the whole field
const getSelectionRange = ($textArea) => {
  const { selectionStart, selectionEnd, value } = $textArea;

  if (selectionStart === selectionEnd && value) {
    return { selectionStart: 0, selectionEnd: value.length };
  }

  return { selectionStart, selectionEnd };
};

// Wraps the current selection with the given markdown markers (e.g. ** for bold), or with a
// distinct pair when the closing marker differs (e.g. (( )) for a note). Like the other
// formatting buttons, it only acts when text is actually selected.
const wrapSelection = (setInputToState, openMarker, closeMarker = openMarker) => {
  const $textArea = document.getElementById(TEXT_AREA_ID);

  const selectionStart = $textArea.selectionStart;
  const selectionEnd = $textArea.selectionEnd;

  if (selectionStart === selectionEnd) return;

  const selectedText = $textArea.value.substring(selectionStart, selectionEnd);
  const updatedSelectedText = `${openMarker}${selectedText}${closeMarker}`;

  // Replace the selected text with the wrapped text
  const textAreaContent = replaceTextAt($textArea.value, selectionStart, updatedSelectedText, selectedText.length);

  setInputToState({
    currentTarget: {
      name: TEXT_AREA_ID,
      value: textAreaContent,
    },
  });

  // Keep the original text selected, now between the inserted markers
  setTimeout(() => {
    $textArea.focus();
    $textArea.setSelectionRange(selectionStart + openMarker.length, selectionEnd + openMarker.length);
  }, 0);
};

export const handleBoldButton = (setInputToState) => wrapSelection(setInputToState, '**');

export const handleItalicButton = (setInputToState) => wrapSelection(setInputToState, '*');

export const handleUnderlineButton = (setInputToState) => wrapSelection(setInputToState, '__');

// Markdown's own note marker is the blockquote '>', but the save-path sanitizer in SongForm
// escapes '>' to '&gt;', so a note written that way would not survive a round-trip. '(('
// already reads as an aside in plain text and passes through untouched.
export const handleMakeNoteButton = (setInputToState) => wrapSelection(setInputToState, '((', '))');

export const handleExpandButton = (setInputToState) => {
  const $textArea = document.getElementById(TEXT_AREA_ID);

  const { selectionStart, selectionEnd } = getSelectionRange($textArea);

  if (selectionStart !== selectionEnd) {
    let selectedText = $textArea.value.substring(selectionStart, selectionEnd);

    selectedText = selectedText.replaceAll(/\n/g, '\n\n');
    selectedText = `\n${selectedText}`;

    const textAreaContent = replaceTextAt($textArea.value, selectionStart, selectedText, selectionEnd - selectionStart);

    setInputToState({
      currentTarget: {
        name: TEXT_AREA_ID,
        value: textAreaContent,
      },
    });

    // Set the cursor position
    setTimeout(() => {
      $textArea.focus();
      $textArea.setSelectionRange(selectionStart, selectionStart + selectedText.length);
    }, 0);
  }
};

/**
 * A half step up or down through every chord in a block of text.
 *
 * Split out of the two buttons below because the textarea is no longer the only place a part's
 * chords live: a part made of words keeps its chart off its lyrics, and it has to move by the
 * same interval or the chart is left in a key the words have gone from.
 */
export const transposeChordText = (text, songKey, direction) =>
  songKey ? replaceChords(String(text ?? ''), (chord) => transpose(chord, songKey, 1, direction)) : text;

export const handleTransposeUpButton = (setInputToState, songKey) => {
  if (!songKey) return;

  const $textArea = document.getElementById(TEXT_AREA_ID);
  const textAreaContent = transposeChordText($textArea.value, songKey, 'up');

  setInputToState({
    currentTarget: {
      name: TEXT_AREA_ID,
      value: textAreaContent,
    },
  });

  // Maintain cursor position
  setTimeout(() => {
    $textArea.focus();
    $textArea.setSelectionRange($textArea.selectionStart, $textArea.selectionEnd);
  }, 0);
};

export const handleTransposeDownButton = (setInputToState, songKey) => {
  if (!songKey) return;

  const $textArea = document.getElementById(TEXT_AREA_ID);
  const textAreaContent = transposeChordText($textArea.value, songKey, 'down');

  setInputToState({
    currentTarget: {
      name: TEXT_AREA_ID,
      value: textAreaContent,
    },
  });

  // Maintain cursor position
  setTimeout(() => {
    $textArea.focus();
    $textArea.setSelectionRange($textArea.selectionStart, $textArea.selectionEnd);
  }, 0);
};
